// @ts-ignore
import "./devmode.banner.scss";
import React, { useEffect, useState } from "react";
import { aiConfig } from "me-at-eon/src";
import { languages } from "me-at-eon/src/components/language-switch/LangInterface";
import { EnvironmentConfig } from "@me8eon/config";
import { EonUiPreloaderCircle } from '@eon-ui/eon-ui-components-react';

const BANNER_ID = "MYHELP_BANNER";
const API_BASE_URL = EnvironmentConfig.nodeService;
const GET_PREF_ENDPOINT = `${API_BASE_URL}/service/getGlobalData`;
const SET_PREF_ENDPOINT = `${API_BASE_URL}/service/setGlobalData`;

const bannerTranslationsPrompt = (
    text: Partial<BannerData>[]
) => `Translate the 'content' field in the below JSON object into languages ${languages.map((i) => `${i.name} (${i.code})`).join(", ")}.
Please maintain the JSON and HTML formatting. Answer only with the translated JSON object. No more formatting or explanation, just the JSON.
Below is the JSON object to translate of the format { "id": string, "content": string }[], where the content field may contain HTML that also needs to be preserved and translated.
Reply with the translated JSON object of the format { "id": string, "content": string, "content_code": translated_content }[], where content_code is the key for each language translation, e.g. content_de for German translation, don't modify the id field.
JSON to translate:
${JSON.stringify(text)}`;

async function getTranslations(
    list: Partial<BannerData>[]
): Promise<Partial<BannerData>[]> {
    const { axios, baseURL, jwtTokenFn, customisation } = aiConfig;
    const jwtToken = await jwtTokenFn();
    const axiosInstance = axios.create({
        baseURL,
        headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
        },
    });
    try {
        const response = await axiosInstance.post(
            `/service/completions`,
            {
                messages: [
                    {
                        role: "system",
                        content:
`You are a helpful assistant that translates text to multiple languages.
Respond only with the translated JSON, no explanations or additional text.
The JSON string received should be parsed into an JSON array.
`,
                    },
                    { role: "user", content: bannerTranslationsPrompt(list) },
                ],
                ...customisation,
            }
        );
        const translations = response.data?.choices?.[0]?.message?.content;
        if (!translations) return list;

        const tryParseTranslations = (text: string): any => {
            // Try direct parse first
            try {
                return JSON.parse(text);
            } catch (e) {}

            // Strip common code-fence wrappers like ```json ... ```
            let cleaned = text.replace(/^\s*```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();

            // Try parsing cleaned text
            try {
                return JSON.parse(cleaned);
            } catch (e) {}

            // Attempt to extract the first JSON array or object by brackets
            const firstArrayStart = cleaned.indexOf("[");
            const lastArrayEnd = cleaned.lastIndexOf("]");
            if (firstArrayStart !== -1 && lastArrayEnd !== -1 && lastArrayEnd > firstArrayStart) {
                const sub = cleaned.substring(firstArrayStart, lastArrayEnd + 1);
                try {
                    return JSON.parse(sub);
                } catch (e) {}
            }

            // Fallback: try to find a JSON array via regex
            const match = cleaned.match(/(\[\s*\{[\s\S]*\}\s*\])/);
            if (match && match[1]) {
                try {
                    return JSON.parse(match[1]);
                } catch (e) {}
            }

            // If all else fails, return null so caller can decide fallback
            return null;
        };

        const parsedTranslations = tryParseTranslations(translations) || list;
        return parsedTranslations;
    } catch (error) {
        console.error("getTranslations error:", error);
        return list;
    }
}

export const getBannerData = async (jwtTokenFn: any): Promise<BannerData[]> => {
    try {
        const token = await jwtTokenFn();
        const response = await fetch(GET_PREF_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                User: BANNER_ID,
            }),
        });
        if (!response.ok) {
            throw new Error(
                `Failed to fetch banner data: ${response.statusText}`
            );
        }
        const data: { bannerData?: string } = await response.json();
        return JSON.parse(data.bannerData ?? "[]") as BannerData[];
    } catch (error) {
        console.error("Error in getBannerData:", error);
        throw error;
    }
};

export const saveBannerData = async (
    updatedData: BannerData[],
    jwtTokenFn: any
): Promise<void> => {
    try {
        const token = await jwtTokenFn();
        const response = await fetch(SET_PREF_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                User: BANNER_ID,
                bannerData: JSON.stringify(updatedData),
            }),
        });
        if (!response.ok) {
            const errorBody = await response.json();
            throw new Error(
                `Failed to set banner data: ${response.status} ${response.statusText}${errorBody?.error ? ` - ${errorBody.error}` : ""}`
            );
        }
        const data: { isSuccess: boolean } = await response.json();
        if (data.isSuccess !== true) {
            console.warn(
                "Set data succeeded, but response not marked as success."
            );
        }
    } catch (error) {
        console.error("Error in saveBannerData:", error);
        throw error;
    }
};

export interface BannerData {
    id: string;
    content: string;
    timestamp: number;
    active: boolean;
    icon?: "" | "info" | "alert";
    start?: string | null;
    end?: string | null;
    reoccurrence: "daily" | "weekly" | "none" | null;
    edited?: boolean;
}

const getUpdatedBannerInfo = async (index: number): Promise<Partial<BannerData>> => {
    const timestamp = Date.now();
    const uniqueKey = crypto.randomUUID();
    const sanitizedKey = uniqueKey.replace(/[^a-zA-Z0-9]/g, "");
    return {
        id: sanitizedKey,
        timestamp,
    };
}

const getNewBannerData = async (): Promise<BannerData> => {
    const timestamp = Date.now();
    const uniqueKey = crypto.randomUUID();
    const sanitizedKey = uniqueKey.replace(/[^a-zA-Z0-9]/g, "");
    return {
        id: sanitizedKey,
        content: "",
        timestamp,
        active: true,
        icon: "info",
        start: null,
        end: null,
        reoccurrence: "none",
    };
};

export function DevModeBanner() {
    const [list, setList] = useState<BannerData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { jwtTokenFn } = aiConfig;
    const refresh = () => {
        getBannerData(jwtTokenFn)
            .then(setList)
            .catch((err) => console.error("Failed to fetch banner data:", err));
    };
    const save = () => {
        setLoading(true);
        getTranslations(list.map(({ content, id }) => ({ content, id })))
            .then(async (translated: any) => {
                const translatedList = await Promise.all(
                    list.map(async (item, index) => {
                        const translationEntry = translated.find(
                            (t: any) => t.id === item.id
                        );
                        const { edited, ...itemWithoutEdited } = item;
                        const newInfo = {};
                        if (edited) {
                            await getUpdatedBannerInfo(index).then((updatedInfo) => {
                                Object.assign(newInfo, updatedInfo);
                            });
                        }
                        return translationEntry
                            ? { ...itemWithoutEdited, ...translationEntry, ...newInfo }
                            : { ...itemWithoutEdited, ...newInfo };
                    })
                );
                console.log("Translated List:", translatedList);
                setList(translatedList);
                saveBannerData(translatedList, jwtTokenFn)
                    .then((_r) => alert("Banner Saved!"))
                    .catch((err) => {
                        console.error("Failed to save banner data:", err);
                        alert(
                            err?.message ??
                            "Failed to save banner data. Please try again."
                        );
                    })
                    .finally(() => setLoading(false));
            })
            .catch((err) => {
                console.error("Failed to get translations:", err);
                setLoading(false);
            });
    };
    const add = async () => setList([...list, await getNewBannerData()]);
    useEffect(() => {
        refresh();
    }, []);

    return (
        <div className="devmode-banner">
            {loading && <div className="devmode-banner--loader">
                <EonUiPreloaderCircle />
            </div>}
            <div className="devmode-banner--action-bar">
                <h2>Banner</h2>
                <button onClick={add}>+ Add</button>
            </div>
            {list.map((item, i) => (
                <div key={item.id} className="devmode-banner--info-item">
                    <textarea
                        placeholder="Type something..."
                        value={item.content}
                        onChange={(e) =>
                            setList((old) =>
                                old.map((x, j) =>
                                    j === i
                                        ? { ...x, content: e.target.value, edited: true }
                                        : x
                                )
                            )
                        }
                        rows={3}
                    />
                    <button
                        onClick={() =>
                            setList((old) => old.filter((_, j) => j !== i))
                        }
                    >
                        Delete
                    </button>
                    <div className="devmode-banner--info-item--input">
                        <label>Active:</label>
                        <input
                            type="checkbox"
                            checked={item.active}
                            onChange={(e) =>
                                setList((old) =>
                                    old.map((x, j) =>
                                        j === i
                                            ? { ...x, active: e.target.checked, edited: true }
                                            : x
                                    )
                                )
                            }
                        />
                    </div>
                    <div className="devmode-banner--info-item--input">
                        <label>Start Date (in CET): </label>
                        <input
                            type="datetime-local"
                            value={item.start ?? ""}
                            onChange={(e) =>
                                setList((old) =>
                                    old.map((x, j) =>
                                        j === i
                                            ? {
                                                ...x,
                                                start: e.target.value,
                                                edited: true,
                                            }
                                            : x
                                    )
                                )
                            }
                        />
                        <label>End Date (in CET): </label>
                        <input
                            type="datetime-local"
                            value={item.end ?? ""}
                            onChange={(e) =>
                                setList((old) =>
                                    old.map((x, j) =>
                                        j === i
                                            ? {
                                                ...x,
                                                end: e.target.value,
                                                edited: true,
                                            }
                                            : x
                                    )
                                )
                            }
                        />
                    </div>
                    <div className="devmode-banner--info-item--input">
                        <label>Icon:</label>
                        <select
                            value={item?.icon ?? ""}
                            onChange={(e) =>
                                setList((old) =>
                                    old.map((x, j) =>
                                        j === i
                                            ? {
                                                ...x,
                                                icon: e.target.value as any,
                                                edited: true,
                                            }
                                            : x
                                    )
                                )
                            }
                        >
                            <option value="">Select Icon</option>
                            <option value="info">Info</option>
                            <option value="alert">Alert</option>
                        </select>
                        <label>Reoccurrence:</label>
                        <select
                            value={item?.reoccurrence ?? ""}
                            onChange={(e) =>
                                setList((old) =>
                                    old.map((x, j) =>
                                        j === i
                                            ? {
                                                ...x,
                                                reoccurrence: e.target.value as any,
                                                edited: true,
                                            }
                                            : x
                                    )
                                )
                            }
                        >
                            <option value="">Select Reoccurrence</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="none">None</option>
                        </select>
                    </div>
                    <div>
                        Preview:
                        <br />
                        <div
                            dangerouslySetInnerHTML={{ __html: item.content }}
                        />
                    </div>
                </div>
            ))}
            <div className="devmode-banner--action-bar">
                <h2 />
                <button onClick={refresh}>⟳ Refresh</button>
                <button onClick={save} disabled={loading}>
                    ✓ {loading ? "Saving" : "Save"}
                </button>
            </div>
        </div>
    );
}
